import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminStore } from '../../stores/adminStore';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../table';
import FilterReportBar from '../FilterReportBar';
import { Report } from '../../types/report';
import { Button } from '../common/Button';
import ReportedModalInfo from './ReportedModalInfo';
import { getReporterDisplay } from './reporterDisplay';

const ReportList: React.FC = () => {
  const { t } = useTranslation(['common', 'reports', 'admin']);
  const { reports, fetchReports, fetchMoreReports, hasMore } = useAdminStore();
  const [filters, setFilters] = useState({ status: '', content_type: '' });

  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchReports({ filters }).finally(() => setLoading(false));
  }, [filters, fetchReports]);

  const handleFilterChange = (name: string, value: string) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleRowClick = (report: Report) => {
    setSelectedReport(report);
    setIsModalOpen(true);
  };

  const handleLoadMore = async () => {
    setLoading(true);
    await fetchMoreReports();
    setLoading(false);
  };

  return (
    <>
      <div className="w-full pt-4 pb-5 flex flex-col md:w-2/3 md:px-1 md:pr-8 md:pb-8">
        <h1 className="text-5xl font-bold hyphens-auto">
          {t('navigation.reports', { ns: 'admin' })}
        </h1>
      </div>
      {reports.length > 0 ? (
        <div className="overflow-hidden pb-4 rounded-2xl bg-white border border-gray-300 shadow-sm transition-all">
          <FilterReportBar filters={filters} onFilterChange={handleFilterChange} />
          <div className="max-w-full overflow-x-auto">
            <Table className="w-full">
              <TableHeader className="bg-gray-50">
                <TableRow>
                  {[
                    'reporter',
                    'reporter_email',
                    'reported',
                    'reported_content',
                    'reason',
                    'status',
                    'created_at',
                  ].map(field => (
                    <TableCell
                      key={field}
                      isHeader
                      className="text-start px-6 py-4 font-semibold uppercase text-gray-600 text-[0.75rem] tracking-wide"
                    >
                      {t(`table_fields.${field}`, { ns: 'admin' })}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHeader>

              <TableBody className="divide-y divide-gray-100">
                {reports.map(report => (
                  <TableRow
                    key={report.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleRowClick(report)}
                  >
                    <TableCell className="px-6 py-4">
                      {getReporterDisplay(report, t, { includeEmailFallback: false })}
                    </TableCell>
                    <TableCell className="px-6 py-4">{report.reporter_email || '-'}</TableCell>
                    <TableCell className="px-6 py-4">
                      {report.content_type === 'issue'
                        ? '-'
                        : report.reported
                          ? `${report.reported.first_name} ${report.reported.last_name ?? ''}`.trim()
                          : '-'}
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      {t(`content_type.${report.content_type}`, { ns: 'common' })}
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      {report.content_type === 'issue'
                        ? (report.issue_title ?? t('report_reasons.product_bug', { ns: 'reports' }))
                        : t(`report_reasons.${report.reason}`, {
                            ns: 'reports',
                            defaultValue: report.reason,
                          })}
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      {t(`report_status.${report.status}`, { ns: 'common' })}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-gray-600">
                      {new Date(report.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {hasMore?.reports && (
            <div>
              <Button
                className="w-full my-4 md:w-auto md:px-8 md:justify-self-center"
                onClick={() => handleLoadMore()}
                disabled={loading}
              >
                {loading
                  ? t('loading', { defaultValue: 'Chargement...', ns: 'common' })
                  : t('loadMore', { defaultValue: 'Charger plus', ns: 'common' })}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center p-6 text-center">
          {t('nothing_to_show', { ns: 'common' })}
        </div>
      )}

      {isModalOpen && selectedReport && (
        <ReportedModalInfo report={selectedReport} onClose={() => setIsModalOpen(false)} />
      )}
    </>
  );
};

export default ReportList;
